import api from './axios'

/**
 * Direct Submissions API
 * For form users to create and manage submissions directly
 */
export const directSubmissionsApi = {
    /**
     * Get direct submissions list with filters
     */
    getAll: async (filters = {}) => {
        const params = new URLSearchParams()

        if (filters.status) params.append('status', filters.status)
        if (filters.search) params.append('search', filters.search)
        if (filters.page) params.append('page', filters.page)
        if (filters.perPage) params.append('per_page', filters.perPage)
        if (filters.archived) params.append('archived', '1')

        const response = await api.get(`/direct-submissions?${params.toString()}`)
        return response
    },

    /**
     * Get stats for direct submissions
     */
    getStats: async () => {
        const response = await api.get('/direct-submissions/stats')
        return response
    },

    /**
     * Create direct submission
     * Supports file uploads via FormData
     */
    create: async (formId, data, language = 'en') => {
        // Check if there are file inputs in the data
        const hasFiles = Object.values(data).some(
            value => value instanceof File || (Array.isArray(value) && value.length > 0 && value[0] instanceof File)
        )

        if (hasFiles) {
            // Use FormData for file uploads
            const formData = new FormData()
            formData.append('form_id', formId)
            formData.append('language', language)

            // Add each field to FormData
            for (const [key, value] of Object.entries(data)) {
                if (value instanceof File) {
                    formData.append(`files[${key}]`, value)
                } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
                    // Multiple files
                    value.forEach((file, idx) => {
                        formData.append(`files[${key}][]`, file)
                    })
                } else if (value !== undefined && value !== null) {
                    // Regular field data
                    formData.append(`data[${key}]`, typeof value === 'object' ? JSON.stringify(value) : value)
                }
            }

            const response = await api.post('/direct-submissions', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            return response
        } else {
            // Regular JSON submission
            const response = await api.post('/direct-submissions', {
                form_id: formId,
                data,
                language
            })
            return response
        }
    },

    /**
     * Update submission status
     */
    updateStatus: async (id, status) => {
        const response = await api.put(`/direct-submissions/${id}/status`, { status })
        return response
    },

    /**
     * Archive a submission
     */
    archive: async (id) => {
        const response = await api.put(`/direct-submissions/${id}/archive`)
        return response
    },

    /**
     * Restore an archived submission
     */
    restore: async (id) => {
        const response = await api.put(`/direct-submissions/${id}/restore`)
        return response
    },

    /**
     * Get internal comments for a submission
     */
    getComments: async (submissionId) => {
        const response = await api.get(`/direct-submissions/${submissionId}/comments`)
        return response
    },

    /**
     * Add internal comment
     */
    addComment: async (submissionId, comment) => {
        const response = await api.post(`/direct-submissions/${submissionId}/comments`, { comment })
        return response
    },

    /**
     * Delete internal comment
     */
    deleteComment: async (commentId) => {
        const response = await api.delete(`/direct-submissions/comments/${commentId}`)
        return response
    },

    /**
     * Live search — queries all submission types (direct, chat, normal) including archived.
     * Returns results filtered by the user's form/department access (handled server-side).
     */
    search: async (query) => {
        const response = await api.get(`/submissions/search?q=${encodeURIComponent(query)}`)
        return response
    },
}

export default directSubmissionsApi
